import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Plus, X, Upload, CheckCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import RatingStars from '@/components/ui/RatingStars';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Textarea from '@/components/ui/Textarea';

interface ReviewFormProps {
  productId: string;
  productSlug: string;
  orderId?: string;
  onSuccess?: () => void;
}

const ATTRIBUTE_KEYS = [
  'germination_rate',
  'seed_quality',
  'packaging',
  'value_for_money',
] as const;

export default function ReviewForm({ productId, productSlug, orderId, onSuccess }: ReviewFormProps) {
  const { t } = useTranslation('product');
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = !!user;
  const addToast = useUIStore((s) => s.addToast);
  const queryClient = useQueryClient();

  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [pros, setPros] = useState<string[]>([]);
  const [cons, setCons] = useState<string[]>([]);
  const [newPro, setNewPro] = useState('');
  const [newCon, setNewCon] = useState('');
  const [attributeRatings, setAttributeRatings] = useState<Record<string, number>>({});
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!isAuthenticated) {
    return (
      <div className="rounded-lg border border-line bg-surface-soft p-6 text-center">
        <Star className="mx-auto h-8 w-8 text-ink-muted mb-2" aria-hidden="true" />
        <p className="text-ink-soft mb-3">{t('reviewForm.loginPrompt')}</p>
        <a
          href="/login"
          className="inline-block rounded-lg bg-sage-700 px-4 py-2 text-sm font-medium text-white hover:bg-sage-800 transition-colors"
        >
          {t('reviewForm.logIn')}
        </a>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="rounded-lg border border-success-200 bg-success-50 p-6 text-center">
        <CheckCircle className="mx-auto h-8 w-8 text-success-600 mb-2" aria-hidden="true" />
        <p className="font-medium text-success-800">{t('reviewForm.thankYou')}</p>
        <p className="text-sm text-success-600 mt-1">{t('reviewForm.thankYouDetail')}</p>
      </div>
    );
  }

  function addPro() {
    if (newPro.trim() && pros.length < 5) {
      setPros([...pros, newPro.trim()]);
      setNewPro('');
    }
  }

  function addCon() {
    if (newCon.trim() && cons.length < 5) {
      setCons([...cons, newCon.trim()]);
      setNewCon('');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (rating < 1) newErrors.rating = t('reviewForm.ratingRequired');
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const reviewData: Record<string, unknown> = {
        product_id: productId,
        rating,
        title: title.trim() || undefined,
        body: body.trim() || undefined,
      };

      if (orderId) reviewData.order_id = orderId;
      if (pros.length > 0) reviewData.pros = pros;
      if (cons.length > 0) reviewData.cons = cons;
      if (Object.keys(attributeRatings).length > 0) reviewData.attribute_ratings = attributeRatings;

      const review = await api.post<any>('/reviews', reviewData);

      // Upload images
      for (const url of imageUrls) {
        try {
          await api.post(`/reviews/${review.id}/images`, { url });
        } catch {
          // non-critical
        }
      }

      setSubmitted(true);
      queryClient.invalidateQueries({ queryKey: ['product-reviews', productSlug] });
      addToast(t('reviewForm.submitSuccess'), 'success');
      onSuccess?.();
    } catch (err: any) {
      addToast(err.message || t('reviewForm.submitFailed'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-line p-6" noValidate>
      <h3 className="text-lg font-semibold text-ink mb-4">{t('reviewForm.heading')}</h3>

      <div className="space-y-5">
        {/* Rating */}
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-2">
            {t('reviewForm.ratingLabel')} <span className="text-red-500">*</span>
          </label>
          <RatingStars rating={rating} interactive onChange={setRating} size="lg" />
          {errors.rating && (
            <p className="mt-1 text-sm text-red-600" role="alert">{errors.rating}</p>
          )}
        </div>

        {/* Title */}
        <Input
          label={t('reviewForm.titleLabel')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('reviewForm.titlePlaceholder')}
          maxLength={200}
        />

        {/* Body */}
        <Textarea
          label={t('reviewForm.bodyLabel')}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t('reviewForm.bodyPlaceholder')}
          rows={4}
          maxLength={5000}
        />

        {/* Pros */}
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-2">{t('reviewForm.prosLabel')}</label>
          <div className="space-y-1.5">
            {pros.map((pro, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-success-700">
                <CheckCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="flex-1">{pro}</span>
                <button
                  type="button"
                  onClick={() => setPros(pros.filter((_, j) => j !== i))}
                  className="text-ink-muted hover:text-ink-soft"
                  aria-label={t('reviewForm.removeProAriaLabel', { value: pro })}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          {pros.length < 5 && (
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={newPro}
                onChange={(e) => setNewPro(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPro(); } }}
                placeholder={t('reviewForm.addProPlaceholder')}
                className="flex-1 rounded-md border border-line-strong px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
                maxLength={200}
              />
              <button
                type="button"
                onClick={addPro}
                className="flex items-center gap-1 rounded-md bg-sage-50 px-3 py-1.5 text-sm text-sage-700 hover:bg-sage-100"
              >
                <Plus className="h-3.5 w-3.5" /> {t('reviewForm.addButton')}
              </button>
            </div>
          )}
        </div>

        {/* Cons */}
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-2">{t('reviewForm.consLabel')}</label>
          <div className="space-y-1.5">
            {cons.map((con, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-red-600">
                <X className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="flex-1">{con}</span>
                <button
                  type="button"
                  onClick={() => setCons(cons.filter((_, j) => j !== i))}
                  className="text-ink-muted hover:text-ink-soft"
                  aria-label={t('reviewForm.removeConAriaLabel', { value: con })}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
          {cons.length < 5 && (
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={newCon}
                onChange={(e) => setNewCon(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCon(); } }}
                placeholder={t('reviewForm.addConPlaceholder')}
                className="flex-1 rounded-md border border-line-strong px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
                maxLength={200}
              />
              <button
                type="button"
                onClick={addCon}
                className="flex items-center gap-1 rounded-md bg-red-50 px-3 py-1.5 text-sm text-red-600 hover:bg-red-100"
              >
                <Plus className="h-3.5 w-3.5" /> {t('reviewForm.addButton')}
              </button>
            </div>
          )}
        </div>

        {/* Attribute ratings */}
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-3">
            {t('reviewForm.attributesLabel')}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ATTRIBUTE_KEYS.map((key) => (
              <div key={key} className="flex items-center justify-between gap-2">
                <span className="text-sm text-ink-soft">
                  {t(`reviewForm.attributes.${key}`)}
                </span>
                <RatingStars
                  rating={attributeRatings[key] || 0}
                  interactive
                  onChange={(val) => setAttributeRatings({ ...attributeRatings, [key]: val })}
                  size="sm"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Image URLs */}
        <div>
          <label className="block text-sm font-medium text-ink-soft mb-2">
            {t('reviewForm.photosLabel', { count: imageUrls.length })}
          </label>
          <div className="flex flex-wrap gap-2">
            {imageUrls.map((url, i) => (
              <div key={i} className="relative w-20 h-20 rounded-md overflow-hidden border border-line">
                <img src={url} alt={t('reviewForm.photoAlt', { index: i + 1 })} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setImageUrls(imageUrls.filter((_, j) => j !== i))}
                  className="absolute top-0.5 right-0.5 bg-black/50 rounded-full p-0.5 text-white"
                  aria-label={t('reviewForm.removePhoto')}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
          {imageUrls.length < 5 && (
            <div className="flex gap-2 mt-2">
              <input
                type="url"
                placeholder={t('reviewForm.photoUrlPlaceholder')}
                className="flex-1 rounded-md border border-line-strong px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = (e.target as HTMLInputElement).value.trim();
                    if (val) {
                      setImageUrls([...imageUrls, val]);
                      (e.target as HTMLInputElement).value = '';
                    }
                  }
                }}
              />
              <div className="flex items-center text-xs text-ink-muted">
                <Upload className="h-3.5 w-3.5 mr-1" />
                {t('reviewForm.enterToAdd')}
              </div>
            </div>
          )}
        </div>

        <Button type="submit" isLoading={isSubmitting} className="w-full sm:w-auto">
          {t('reviewForm.submit')}
        </Button>
      </div>
    </form>
  );
}
